import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Autocomplete,
  Avatar,
  Chip,
  IconButton,
  MenuItem,
} from '@mui/material';
import {
  Send as SendIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { contactsApi, triageApi } from '../../services/api';

interface Contact {
  id: string;
  internal_id?: string;
  internalId?: string;
  first_name?: string;
  firstName?: string;
  last_name?: string;
  lastName?: string;
  full_name?: string;
  fullName?: string;
  phone?: string;
  email?: string;
}

interface NewTaskModalProps {
  open: boolean;
  onClose: () => void;
  onTaskCreated?: () => void;
}

const NewTaskModal: React.FC<NewTaskModalProps> = ({ open, onClose, onTaskCreated }) => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sendStatus, setSendStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [taskType, setTaskType] = useState<'message' | 'internal'>('message');
  const [assignees, setAssignees] = useState<Array<{ id: string | number; name?: string; email?: string }>>([]);
  const [assigneesLoading, setAssigneesLoading] = useState(false);
  const [selectedAssigneeId, setSelectedAssigneeId] = useState<string | number>('');

  // Fetch contacts on mount
  useEffect(() => {
    if (open) {
      fetchContacts();
      fetchAssignees();
      // Reset state when opening
      setSelectedContact(null);
      setMessage('');
      setSendStatus('idle');
      setErrorMessage('');
      setInputValue('');
      setTaskType('message');
      setSelectedAssigneeId('');
    }
  }, [open]);

  const fetchContacts = async () => {
    setIsLoading(true);
    try {
      const response = await contactsApi.getContacts({ limit: 500, isArchived: false });
      const data = response.data?.data || response.data || [];
      setContacts(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAssignees = async () => {
    setAssigneesLoading(true);
    try {
      const response = await triageApi.getAssignableUsers();
      const data = response.data?.data || response.data || [];
      setAssignees(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch assignees:', error);
    } finally {
      setAssigneesLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!selectedContact || !message.trim()) return;
    if (!selectedContact.phone) {
      setErrorMessage('This contact does not have a phone number.');
      setSendStatus('error');
      return;
    }
    setIsSending(true);
    setSendStatus('idle');
    setErrorMessage('');

    try {
      // Build name from available fields (handle both camelCase and snake_case)
      const firstName = selectedContact.firstName || selectedContact.first_name || '';
      const lastName = selectedContact.lastName || selectedContact.last_name || '';
      const fullName = selectedContact.fullName || selectedContact.full_name || '';
      const patientName = fullName || `${firstName} ${lastName}`.trim() || 'Unknown';
      const internalId = selectedContact.internalId || selectedContact.internal_id;

      if (taskType === 'internal') {
        const response = await triageApi.createInternalTask({
          contactId: selectedContact.id,
          patientName,
          patientPhone: selectedContact.phone,
          patientEmail: selectedContact.email,
          internalPatientId: internalId,
          assignedTo: selectedAssigneeId || undefined,
          message: message.trim(),
        });
        if (!response.data?.success) {
          throw new Error(response.data?.error || 'Failed to create internal task');
        }
      } else {
        const response = await triageApi.initiateTask({
          contactId: selectedContact.id,
          patientName,
          patientPhone: selectedContact.phone,
          patientEmail: selectedContact.email,
          internalPatientId: internalId,
          assignedTo: selectedAssigneeId || undefined,
          message: message.trim(),
        });
        if (!response.data?.success) {
          throw new Error(response.data?.error || 'Failed to send message');
        }
      }

      setSendStatus('success');
      setMessage('');

      // Close modal after brief success display
      setTimeout(() => {
        onTaskCreated?.();
        onClose();
      }, 1500);
    } catch (error: any) {
      console.error('Failed to create task:', error);
      setErrorMessage(error.response?.data?.error || error.message || 'Failed to create task');
      setSendStatus('error');
    } finally {
      setIsSending(false);
    }
  };

  const handleClose = () => {
    setSelectedContact(null);
    setMessage('');
    setSendStatus('idle');
    setErrorMessage('');
    setInputValue('');
    setTaskType('message');
    setSelectedAssigneeId('');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="new-task-dialog-title"
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: 3,
          overflow: 'hidden',
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        id="new-task-dialog-title"
        sx={{
          background: 'linear-gradient(135deg, #1976d2 0%, #5e35b1 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              bgcolor: 'rgba(255,255,255,0.2)',
              borderRadius: 2,
              p: 1,
              display: 'flex',
            }}
          >
            <SendIcon />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {taskType === 'internal' ? 'Internal Task' : 'New Task'}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {taskType === 'internal'
                ? 'Create an internal task without sending a patient message'
                : 'Start a conversation with a patient'}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={handleClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {/* Task Type */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
            Task Type
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant={taskType === 'message' ? 'contained' : 'outlined'}
              onClick={() => setTaskType('message')}
              sx={{ flex: 1 }}
            >
              PT Message Task
            </Button>
            <Button
              variant={taskType === 'internal' ? 'contained' : 'outlined'}
              onClick={() => setTaskType('internal')}
              sx={{ flex: 1 }}
            >
              Internal Task
            </Button>
          </Box>
          {taskType === 'internal' && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Internal tasks do not send a message to the patient.
            </Alert>
          )}
        </Box>

        {/* Contact Search */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
            Search Patient
          </Typography>
          <Autocomplete
            options={contacts}
            loading={isLoading}
            value={selectedContact}
            inputValue={inputValue}
            onInputChange={(_, newValue) => setInputValue(newValue)}
            onChange={(_, newValue) => {
              setSelectedContact(newValue);
              setSendStatus('idle');
              setErrorMessage('');
            }}
            getOptionLabel={(option) => {
              // Build display name from available fields (handle both camelCase and snake_case)
              const firstName = option.firstName || option.first_name || '';
              const lastName = option.lastName || option.last_name || '';
              const fullName = option.fullName || option.full_name || '';
              const name = fullName || `${firstName} ${lastName}`.trim();
              return name || option.phone || 'Unknown';
            }}
            filterOptions={(options, { inputValue }) => {
              const lowerInput = inputValue.toLowerCase();
              return options.filter(
                (option) => {
                  const firstName = option.firstName || option.first_name || '';
                  const lastName = option.lastName || option.last_name || '';
                  const fullName = option.fullName || option.full_name || '';
                  const name = fullName || `${firstName} ${lastName}`.trim();
                  const internalId = option.internalId || option.internal_id || '';
                  return name?.toLowerCase().includes(lowerInput) ||
                    option.phone?.includes(inputValue) ||
                    option.email?.toLowerCase().includes(lowerInput) ||
                    internalId?.toLowerCase().includes(lowerInput);
                }
              ).slice(0, 10);
            }}
            renderOption={(props, option) => {
              // Build display name from available fields (handle both camelCase and snake_case)
              const firstName = option.firstName || option.first_name || '';
              const lastName = option.lastName || option.last_name || '';
              const fullName = option.fullName || option.full_name || '';
              const displayName = fullName || `${firstName} ${lastName}`.trim() || 'Unknown';
              const internalId = option.internalId || option.internal_id;
              return (
                <li {...props} key={option.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1, width: '100%' }}>
                    <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
                      <PersonIcon />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        {displayName}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        {internalId && (
                          <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 600 }}>
                            {internalId}
                          </Typography>
                        )}
                        {option.phone && (
                          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <PhoneIcon sx={{ fontSize: 12 }} />
                            {option.phone}
                          </Typography>
                        )}
                        {option.email && (
                          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <EmailIcon sx={{ fontSize: 12 }} />
                            {option.email}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search by name, phone, email, or ID..."
                variant="outlined"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Box>

        {/* Selected Contact Card */}
        {selectedContact && (() => {
          // Build display values (handle both camelCase and snake_case)
          const firstName = selectedContact.firstName || selectedContact.first_name || '';
          const lastName = selectedContact.lastName || selectedContact.last_name || '';
          const fullName = selectedContact.fullName || selectedContact.full_name || '';
          const contactName = fullName || `${firstName} ${lastName}`.trim() || 'Unknown';
          const contactInternalId = selectedContact.internalId || selectedContact.internal_id;
          
          return (
          <Box
            sx={{
              mb: 3,
              p: 2,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #e3f2fd 0%, #ede7f6 100%)',
              border: '1px solid',
              borderColor: 'primary.light',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
                  <PersonIcon />
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {contactName}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {contactInternalId && (
                      <Chip
                        label={contactInternalId}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ fontWeight: 600 }}
                      />
                    )}
                    {selectedContact.phone && (
                      <Typography variant="body2" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <PhoneIcon sx={{ fontSize: 14 }} />
                        {selectedContact.phone}
                      </Typography>
                    )}
                  </Box>
                  {selectedContact.email && (
                    <Typography variant="body2" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                      <EmailIcon sx={{ fontSize: 14 }} />
                      {selectedContact.email}
                    </Typography>
                  )}
                </Box>
              </Box>
              <IconButton
                size="small"
                onClick={() => {
                  setSelectedContact(null);
                  setInputValue('');
                  setSendStatus('idle');
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
          );
        })()}

        {taskType === 'internal' && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
              Assign To (optional)
            </Typography>
            <TextField
              select
              fullWidth
              value={selectedAssigneeId}
              onChange={(e) => setSelectedAssigneeId(e.target.value)}
              disabled={assigneesLoading}
              placeholder="Select a user"
            >
              {assigneesLoading && (
                <MenuItem value="" disabled>
                  Loading users...
                </MenuItem>
              )}
              {!assigneesLoading && assignees.length === 0 && (
                <MenuItem value="" disabled>
                  No users available
                </MenuItem>
              )}
              {assignees.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.name || user.email}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        )}

        {/* Message Input */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
            {taskType === 'internal' ? 'Internal Note' : 'Message'}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={taskType === 'internal'
              ? 'Type the internal task details...'
              : 'Type your message to the patient...'}
            disabled={!selectedContact || isSending}
            variant="outlined"
          />
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', textAlign: 'right', mt: 0.5 }}>
            {message.length} characters
          </Typography>
        </Box>

        {/* Status Messages */}
        {sendStatus === 'success' && (
          <Alert
            severity="success"
            icon={<CheckCircleIcon />}
            sx={{ borderRadius: 2 }}
          >
            {taskType === 'internal'
              ? 'Internal task created successfully!'
              : 'Message sent and task created successfully!'}
          </Alert>
        )}

        {sendStatus === 'error' && (
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            {errorMessage || 'Failed to send message. Please try again.'}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={handleClose} variant="outlined" sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          onClick={handleSendMessage}
          variant="contained"
          disabled={!selectedContact || !message.trim() || isSending}
          startIcon={isSending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            background: 'linear-gradient(135deg, #1976d2 0%, #5e35b1 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #1565c0 0%, #4527a0 100%)',
            },
          }}
        >
          {isSending
            ? 'Saving...'
            : taskType === 'internal'
              ? 'Create Internal Task'
              : 'Send & Create Task'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewTaskModal;

